import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { logoutThunk } from '../store/auth/authThunk';
import dizeeLogo from '../assets/images/dizee logo.svg';
import { desktopTheme as t } from '../styles/desktopTheme';

const SidebarContainer = styled.aside`
  width: 100%;
  height: 100vh;
  background-color: ${t.bg};
  border-right: 1px solid ${t.borderSubtle};
  display: flex;
  flex-direction: column;
  padding: ${p => (p.$collapsed ? '16px 10px' : '20px 16px')};
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const TopRow = styled.div`
  margin: ${p => (p.$collapsed ? '-16px -10px 10px -10px' : '-20px -16px 16px -16px')};
  padding: ${p => (p.$collapsed ? '16px 10px' : '24px 16px')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${t.borderSubtle};
`;

const Logo = styled.img`
  width: ${p => (p.$collapsed ? '28px' : '50px')};
  height: auto;
  opacity: 0.95;
`;

const CollapseButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid ${t.borderSubtle};
  background: rgba(255, 255, 255, 0.02);
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${t.borderStrong};
  }
`;

const CollapseGlyph = styled.div`
  width: 14px;
  height: 10px;
  display: grid;
  gap: 2px;

  span {
    display: block;
    height: 2px;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 2px;
  }
`;

const RailNavButton = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid transparent;
  background: ${p => (p.$active ? 'rgba(255,255,255,0.08)' : 'transparent')};
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${t.borderSubtle};
  }
`;

const RailSpacer = styled.div`
  height: 10px;
`;

const Chevron = styled.svg`
  width: 12px;
  height: 12px;
  color: ${t.textSecondary};
  transition: transform 0.2s ease-in-out;
  transform: rotate(${props => (props.$expanded ? 90 : 0)}deg);
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${t.textPrimary};
  margin-bottom: 4px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${props => props.type === 'pro' ? 'linear-gradient(135deg, #FFD700, #FFA500)' : '#333'};
  color: ${props => props.type === 'pro' ? '#000' : '#fff'};
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  text-transform: uppercase;
`;

const UsageSection = styled.div`
  margin-bottom: 24px;
`;

const UsageLabel = styled.div`
  font-size: 11px;
  color: ${t.textSecondary};
  margin-bottom: 8px;
`;

const UsageBar = styled.div`
  height: 4px;
  background: rgba(255, 255, 255, 0.10);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const UsageFill = styled.div`
  height: 100%;
  background: ${props => (props.overLimit ? t.accent : '#4CAF50')};
  width: ${props => Math.min(props.percentage, 100)}%;
  transition: width 0.3s ease;
`;

const UsageAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  border-radius: 6px;
  font-size: 11px;
  color: ${t.accent};
`;

const NavSection = styled.div`
  margin-bottom: 24px;
`;

const NavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const NavTitle = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  font-weight: 500;
`;

const NavCount = styled.span`
  font-size: 11px;
  color: ${t.textTertiary};
  margin-left: 8px;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  color: ${props => (props.active ? t.textPrimary : 'rgba(255, 255, 255, 0.70)')};
  background: ${props => (props.active ? 'rgba(255, 255, 255, 0.09)' : 'transparent')};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${t.borderSubtle};
  margin: 16px 0;
`;

const BottomSection = styled.div`
  margin-top: auto;
  padding-top: 16px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
`;

const ToggleLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
`;

const ToggleStatus = styled.span`
  font-size: 10px;
  color: ${t.textTertiary};
`;

const Toggle = styled.button`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${props => (props.active ? t.accent : 'rgba(255, 255, 255, 0.22)')};
  cursor: pointer;
  position: relative;
  transition: background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: ${props => props.active ? '23px' : '3px'};
    transition: left 0.2s ease;
  }
`;

const FeedbackRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 0;
  border-top: 1px solid ${t.borderSubtle};
  margin-top: 6px;
`;

const FeedbackLabel = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  font-size: 12px;
  color: white;
  cursor: pointer;
  opacity: 0.9;

  &:hover {
    opacity: 0.8;
  }
`;

const FeedbackIconButton = styled.button`
  width: 24px;
  height: 24px;
  background: ${t.accent};
  border-radius: 4px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const FeedbackIconText = styled.span`
  color: white;
  font-size: 12px;
  line-height: 1;
`;

const UserFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid ${t.borderSubtle};
  margin-top: 16px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const SmallAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserFooterInfo = styled.div`
  flex: 1;
`;

const UserFooterName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${t.textPrimary};
`;

const UserFooterDomain = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const ExpandArrow = styled.div`
  display: flex;
  align-items: center;
  color: ${t.textSecondary};
`;

const NewChatButton = styled.button`
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #17A2B8;
  border-radius: 6px;
  color: #17A2B8;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(23, 162, 184, 0.1);
  }
`;

export default function DesktopSidebar({ collapsed = false, onToggleCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userArticle } = useSelector((state) => state.user);
  
  const [conversationExpanded, setConversationExpanded] = useState(false);
  const [aiModeEnabled, setAiModeEnabled] = useState(true);
  const [whiteModeEnabled, setWhiteModeEnabled] = useState(false);
  
  // Get user data
  const user = userArticle || JSON.parse(localStorage.getItem('dizeeUser') || '{}');
  const userName = user?.name || user?.username || 'User';
  const userDomain = user?.dizeeName ? `diz.ee/${user.dizeeName}` : '';
  const profilePic = user?.avatar || user?.profilePicture;
  const isPro = user?.subscription?.plan === 'pro';
  
  // Mock usage data - replace with real data
  const usagePercentage = 110; // Over limit example
  const overageAmount = '$82.45';
  const conversationCount = 436;

  const handleLogout = () => {
    dispatch(logoutThunk());
  };

  return (
    <SidebarContainer $collapsed={collapsed}>
      <TopRow $collapsed={collapsed}>
        <Logo $collapsed={collapsed} src={dizeeLogo} alt="DIZEE" />
        <CollapseButton onClick={onToggleCollapse} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <CollapseGlyph>
            <span />
            <span />
            <span />
          </CollapseGlyph>
        </CollapseButton>
      </TopRow>

      {collapsed ? (
        <>
          <RailSpacer />
          <RailNavButton $active={location.pathname.startsWith('/desktop-dashboard')} onClick={() => navigate('/desktop-dashboard')}>
            D
          </RailNavButton>
          <RailNavButton $active={location.pathname.startsWith('/manager')} onClick={() => navigate('/manager')}>
            C
          </RailNavButton>
          <RailNavButton $active={location.pathname.startsWith('/activity-feed')} onClick={() => navigate('/activity-feed')}>
            T
          </RailNavButton>

          <div style={{ marginTop: 'auto' }} />

          <FeedbackRow style={{ justifyContent: 'center' }}>
            <FeedbackIconButton onClick={() => navigate('/feedback')} aria-label="Feedback">
              <FeedbackIconText>?</FeedbackIconText>
            </FeedbackIconButton>
          </FeedbackRow>

          <UserFooter style={{ justifyContent: 'center' }} onClick={() => navigate('/more/setting/profile')}>
            <SmallAvatar>
              {profilePic ? (
                <img src={profilePic} alt={userName} />
              ) : null}
            </SmallAvatar>
          </UserFooter>
        </>
      ) : (
        <>

      {/* User Profile Section */}
      <UserSection>
        <Avatar>
          {profilePic ? (
            <img src={profilePic} alt={userName} />
          ) : null}
        </Avatar>
        <UserInfo>
          <UserName>{userName}</UserName>
          <Badge type={isPro ? 'pro' : 'free'}>
            {isPro ? 'PRO' : 'FREE'}
          </Badge>
        </UserInfo>
      </UserSection>

      {/* Usage Section */}
      <UsageSection>
        <UsageLabel>Over limit</UsageLabel>
        <UsageBar>
          <UsageFill percentage={usagePercentage} overLimit={usagePercentage > 100} />
        </UsageBar>
        {usagePercentage > 100 && (
          <UsageAlert>
            Over limit - {overageAmount} overage charges
          </UsageAlert>
        )}
      </UsageSection>

      {/* Conversation History */}
      <NavSection>
        <NavHeader onClick={() => setConversationExpanded(!conversationExpanded)}>
          <div>
            <NavTitle>Conversation History</NavTitle>
            <NavCount>({conversationCount})</NavCount>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Chevron $expanded={conversationExpanded} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </Chevron>
            <NewChatButton onClick={(e) => { e.stopPropagation(); navigate('/manager'); }}>
              New Chat
            </NewChatButton>
          </div>
        </NavHeader>
      </NavSection>

      <Divider />

      {/* Navigation Items */}
      <NavItem onClick={() => navigate('/more/setting')}>
        Account Setup
      </NavItem>
      
      <NavItem onClick={() => navigate('/sheets')}>
        Sheets
      </NavItem>
      
      <NavItem onClick={() => navigate('/calendar')}>
        Calendar
      </NavItem>

      {/* Bottom Section */}
      <BottomSection>
        <ToggleRow>
          <div>
            <ToggleLabel>AI Mode</ToggleLabel>
            <br />
            <ToggleStatus>{aiModeEnabled ? 'Enabled' : 'Disabled'}</ToggleStatus>
          </div>
          <Toggle 
            active={aiModeEnabled} 
            onClick={() => setAiModeEnabled(!aiModeEnabled)} 
          />
        </ToggleRow>

        <ToggleRow>
          <div>
            <ToggleLabel>White Mode</ToggleLabel>
            <br />
            <ToggleStatus>{whiteModeEnabled ? 'Enabled' : 'Disabled'}</ToggleStatus>
          </div>
          <Toggle 
            active={whiteModeEnabled} 
            onClick={() => setWhiteModeEnabled(!whiteModeEnabled)} 
          />
        </ToggleRow>

        <FeedbackRow>
          <FeedbackLabel onClick={() => navigate('/feedback')}>Feedback</FeedbackLabel>
          <FeedbackIconButton onClick={() => navigate('/feedback')} aria-label="Feedback">
            <FeedbackIconText>?</FeedbackIconText>
          </FeedbackIconButton>
        </FeedbackRow>

        {/* User Footer */}
        <UserFooter onClick={() => navigate('/more/setting/profile')}>
          <SmallAvatar>
            {profilePic ? (
              <img src={profilePic} alt={userName} />
            ) : null}
          </SmallAvatar>
          <UserFooterInfo>
            <UserFooterName>{userName}</UserFooterName>
            {userDomain && <UserFooterDomain>{userDomain}</UserFooterDomain>}
          </UserFooterInfo>
          <ExpandArrow>
            <Chevron $expanded={false} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </Chevron>
          </ExpandArrow>
        </UserFooter>
      </BottomSection>
        </>
      )}
    </SidebarContainer>
  );
}

